import React, { useMemo } from 'react';
import { FlexDiv, TextDefault, TextInput } from './ui';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Checkbox from '@mui/material/Checkbox';
import {
    ensureDecisionBlockActions,
    getDecisionOptionComponent,
    getDecisionOptionScore,
    getOptionActionKey,
    removeDecisionStepActions,
    setDecisionOptionComponent,
    setDecisionOptionCorrect,
    setDecisionOptionScore,
} from './questionActionsEditor';
import { ScoringComponentSelect } from './scoringComponentSelect';

const paperSx = {
    p: 2,
    mb: 1.5,
    borderRadius: 1,
    border: '1px solid',
    borderColor: 'divider',
    bgcolor: 'grey.50',
    boxShadow: 1,
    minWidth: 0,
    maxWidth: '100%',
    boxSizing: 'border-box',
};

function sortedStepIds(decisions) {
    return Object.keys(decisions || {}).sort(
        (a, b) =>
            (decisions[a]?.order ?? 0) - (decisions[b]?.order ?? 0) || a.localeCompare(b)
    );
}

function emptyHeadingRow() {
    return {
        text: '',
        prefix: '',
        scoreFrom: 0,
        scoreTo: 0,
    };
}

/**
 * Decision tree: `block.decisions` + nested `actions[actionId].decisions[stepId].values`.
 */
export function DecisionBlockEditor({
    selectedBlock,
    question,
    scoringComponentOptions,
    onPatchQuestion,
    onUpdateBlock,
}) {
    if (selectedBlock?.type !== 'decision') {
        return null;
    }

    const decisions = selectedBlock.decisions || {};
    const stepIds = useMemo(() => sortedStepIds(decisions), [decisions]);
    const answer = selectedBlock.answer || {};
    const heading = Array.isArray(answer.heading) ? answer.heading : [];

    const patchAnswer = (patch) => {
        onUpdateBlock({ answer: { ...answer, ...patch } });
    };

    const patchHeadingRow = (index, rowPatch) => {
        const next = heading.map((row, i) =>
            i === index ? { ...row, ...rowPatch } : row
        );
        patchAnswer({ heading: next });
    };

    const patchDecisions = (nextDecisions) => {
        onUpdateBlock({ decisions: nextDecisions });
        if (onPatchQuestion) {
            onPatchQuestion((prev) => ({
                ...prev,
                actions: ensureDecisionBlockActions(
                    prev.actions,
                    { ...selectedBlock, decisions: nextDecisions },
                    prev
                ),
            }));
        }
    };

    const addStep = () => {
        const id = `step_${Date.now()}`;
        const maxOrder = Math.max(
            0,
            ...Object.values(decisions).map((s) => Number(s?.order) || 0)
        );
        patchDecisions({
            ...decisions,
            [id]: {
                order: maxOrder + 1,
                question: 'New step',
                options: [
                    { text: 'Option A', value: 'opt_a' },
                    { text: 'Option B', value: 'opt_b' },
                ],
            },
        });
    };

    const removeStep = (stepId) => {
        if (Object.keys(decisions).length <= 1) return;
        const { [stepId]: _, ...rest } = decisions;
        onUpdateBlock({ decisions: rest });
        if (onPatchQuestion && selectedBlock.actionId) {
            onPatchQuestion((prev) => {
                let nextActions = removeDecisionStepActions(
                    prev.actions,
                    selectedBlock.actionId,
                    stepId
                );
                nextActions = ensureDecisionBlockActions(
                    nextActions,
                    { ...selectedBlock, decisions: rest },
                    prev
                );
                return { ...prev, actions: nextActions };
            });
        }
    };

    const patchStep = (stepId, patch) => {
        patchDecisions({
            ...decisions,
            [stepId]: { ...decisions[stepId], ...patch },
        });
    };

    const patchStepOption = (stepId, optIndex, patch) => {
        const opts = [...(decisions[stepId]?.options || [])];
        const cur = opts[optIndex];
        opts[optIndex] =
            typeof cur === 'string'
                ? patch.text != null
                    ? { text: patch.text, value: patch.value ?? cur }
                    : { ...{ text: cur, value: cur }, ...patch }
                : { ...cur, ...patch };
        patchStep(stepId, { options: opts });
    };

    const addStepOption = (stepId) => {
        const opts = [...(decisions[stepId]?.options || [])];
        const n = opts.length + 1;
        opts.push({ text: `Option ${n}`, value: `opt_${Date.now()}` });
        patchStep(stepId, { options: opts });
    };

    const removeStepOption = (stepId, optIndex) => {
        const opts = [...(decisions[stepId]?.options || [])];
        if (opts.length <= 2) return;
        opts.splice(optIndex, 1);
        patchStep(stepId, { options: opts });
    };

    return (
        <FlexDiv
            column
            gap={2}
            $noWrap
            style={{ minWidth: 0, width: '100%' }}
        >
            <TextDefault bold>Decision tree</TextDefault>

            <TextDefault bold>Decision steps</TextDefault>
            <TextDefault lighter>
                Each step has a question and options. Scores live under
                actions → decisions → step → values (by option value).
            </TextDefault>

            {stepIds.map((stepId) => {
                const step = decisions[stepId] || {};
                return (
                    <Paper key={stepId} elevation={0} sx={paperSx}>
                        <Stack spacing={1.5}>
                            <FlexDiv alignCenter gap={8}>
                                <TextField
                                    size="small"
                                    label="Step id"
                                    value={stepId}
                                    disabled
                                    sx={{ flex: 1 }}
                                />
                                <TextField
                                    size="small"
                                    type="number"
                                    label="Order"
                                    value={step.order ?? 0}
                                    onChange={(e) =>
                                        patchStep(stepId, {
                                            order: Number(e.target.value),
                                        })
                                    }
                                    sx={{ width: 100 }}
                                />
                                {Object.keys(decisions).length > 1 ? (
                                    <IconButton
                                        size="small"
                                        aria-label="Remove step"
                                        onClick={() => removeStep(stepId)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>
                                ) : null}
                            </FlexDiv>
                            <TextField
                                size="small"
                                label="Step question"
                                value={step.question || ''}
                                onChange={(e) =>
                                    patchStep(stepId, {
                                        question: e.target.value,
                                    })
                                }
                                fullWidth
                            />
                            {(step.options || []).map((opt, optIndex) => {
                                const optKey = getOptionActionKey(opt, optIndex);
                                const score = getDecisionOptionScore(
                                    question.actions,
                                    selectedBlock.actionId,
                                    stepId,
                                    optKey
                                );
                                const comp = getDecisionOptionComponent(
                                    question.actions,
                                    selectedBlock.actionId,
                                    stepId,
                                    optKey,
                                    question
                                );
                                const row =
                                    question.actions?.[selectedBlock.actionId]
                                        ?.decisions?.[stepId]?.values?.[optKey]?.[0];
                                const isCorrect = Boolean(row?.correct);
                                return (
                                    <Paper
                                        key={optIndex}
                                        variant="outlined"
                                        sx={{ p: 1.5, bgcolor: 'background.paper' }}
                                    >
                                        <Stack spacing={1}>
                                            <FlexDiv gap={8} wrap>
                                                <TextField
                                                    size="small"
                                                    label="Label"
                                                    value={
                                                        typeof opt === 'string'
                                                            ? opt
                                                            : opt?.text ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        patchStepOption(
                                                            stepId,
                                                            optIndex,
                                                            { text: e.target.value }
                                                        )
                                                    }
                                                    sx={{ flex: 1, minWidth: 120 }}
                                                />
                                                <TextField
                                                    size="small"
                                                    label="Value key"
                                                    value={
                                                        typeof opt === 'string'
                                                            ? opt
                                                            : opt?.value ?? ''
                                                    }
                                                    onChange={(e) =>
                                                        patchStepOption(
                                                            stepId,
                                                            optIndex,
                                                            { value: e.target.value }
                                                        )
                                                    }
                                                    sx={{ width: 160 }}
                                                />
                                            </FlexDiv>
                                            <Stack
                                                spacing={1.5}
                                                sx={{ width: '100%', minWidth: 0 }}
                                            >
                                                <TextField
                                                    size="small"
                                                    type="number"
                                                    label="Score"
                                                    value={score}
                                                    onChange={(e) => {
                                                        if (!onPatchQuestion) return;
                                                        onPatchQuestion((prev) => ({
                                                            ...prev,
                                                            actions: setDecisionOptionScore(
                                                                prev.actions,
                                                                selectedBlock.actionId,
                                                                stepId,
                                                                optKey,
                                                                e.target.value,
                                                                prev
                                                            ),
                                                        }));
                                                    }}
                                                    sx={{ width: '100%', maxWidth: 220 }}
                                                />
                                                {onPatchQuestion ? (
                                                    <Box
                                                        sx={{
                                                            width: '100%',
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <ScoringComponentSelect
                                                            componentOptions={
                                                                scoringComponentOptions
                                                            }
                                                            value={comp}
                                                            onChange={(next) =>
                                                                onPatchQuestion((prev) => ({
                                                                    ...prev,
                                                                    actions:
                                                                        setDecisionOptionComponent(
                                                                            prev.actions,
                                                                            selectedBlock.actionId,
                                                                            stepId,
                                                                            optKey,
                                                                            next,
                                                                            prev
                                                                        ),
                                                                }))
                                                            }
                                                        />
                                                    </Box>
                                                ) : null}
                                                <FlexDiv alignCenter gap={4}>
                                                    <Checkbox
                                                        size="small"
                                                        checked={isCorrect}
                                                        onChange={(e) => {
                                                            if (!onPatchQuestion) return;
                                                            onPatchQuestion((prev) => ({
                                                                ...prev,
                                                                actions:
                                                                    setDecisionOptionCorrect(
                                                                        prev.actions,
                                                                        selectedBlock.actionId,
                                                                        stepId,
                                                                        optKey,
                                                                        e.target.checked,
                                                                        prev
                                                                    ),
                                                            }));
                                                        }}
                                                    />
                                                    <TextDefault lighter>
                                                        Correct
                                                    </TextDefault>
                                                </FlexDiv>
                                            </Stack>
                                            {(step.options || []).length > 2 ? (
                                                <IconButton
                                                    size="small"
                                                    onClick={() =>
                                                        removeStepOption(
                                                            stepId,
                                                            optIndex
                                                        )
                                                    }
                                                >
                                                    <DeleteOutlineIcon fontSize="small" />
                                                </IconButton>
                                            ) : null}
                                        </Stack>
                                    </Paper>
                                );
                            })}
                            <FlexDiv>
                                <IconButton
                                    size="small"
                                    onClick={() => addStepOption(stepId)}
                                    aria-label="Add option"
                                >
                                    <AddIcon />
                                </IconButton>
                                <TextDefault lighter>Add option</TextDefault>
                            </FlexDiv>
                        </Stack>
                    </Paper>
                );
            })}
            <FlexDiv>
                <IconButton size="small" onClick={addStep} aria-label="Add step">
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add decision step</TextDefault>
            </FlexDiv>

            <TextDefault bold>Answer (review)</TextDefault>
            {heading.map((row, index) => (
                <Paper key={index} elevation={0} sx={paperSx}>
                    <Stack spacing={1}>
                        <TextField
                            size="small"
                            label="Prefix"
                            value={row.prefix ?? ''}
                            onChange={(e) =>
                                patchHeadingRow(index, {
                                    prefix: e.target.value,
                                })
                            }
                            fullWidth
                        />
                        <TextField
                            size="small"
                            label="Text"
                            value={row.text ?? ''}
                            onChange={(e) =>
                                patchHeadingRow(index, { text: e.target.value })
                            }
                            fullWidth
                            multiline
                            minRows={2}
                        />
                        <FlexDiv gap={8}>
                            <TextField
                                size="small"
                                type="number"
                                label="Score from"
                                value={row.scoreFrom ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        scoreFrom: Number(e.target.value),
                                    })
                                }
                            />
                            <TextField
                                size="small"
                                type="number"
                                label="Score to"
                                value={row.scoreTo ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        scoreTo: Number(e.target.value),
                                    })
                                }
                            />
                            <TextField
                                size="small"
                                label="Description key"
                                value={row.description_key ?? ''}
                                onChange={(e) =>
                                    patchHeadingRow(index, {
                                        description_key: e.target.value,
                                    })
                                }
                                sx={{ flex: 1 }}
                            />
                        </FlexDiv>
                    </Stack>
                </Paper>
            ))}
            <FlexDiv>
                <IconButton
                    size="small"
                    onClick={() =>
                        patchAnswer({
                            heading: [...heading, emptyHeadingRow()],
                        })
                    }
                >
                    <AddIcon />
                </IconButton>
                <TextDefault lighter>Add heading row</TextDefault>
            </FlexDiv>
        </FlexDiv>
    );
}
